import { useQuery } from "@tanstack/react-query";
import { Loader2, PackagePlus, ShoppingCart, TrendingUp, CalendarRange, Warehouse, History } from "lucide-react";
import { fetchExecutiveDashboard } from "@/lib/api";
import { T, Section, Empty, BarList, TopCustomers, SFG_COLOR, FG_COLOR } from "@/components/exec/ExecParts";
import { IntakePivot, MonthlyChart, QuarterTiles, YearlyTable, RmStockTable } from "@/components/exec/ExecWidgets";

const SalesCard = ({ kind, label, color, byGrade, top, total }) => (
  <Section testId={`sales-${kind}`} icon={ShoppingCart} eyebrow="Sales · this month" title={`${label} sales`}
    right={<T kg={total} testId={`sales-${kind}-total`} className="text-2xl font-black text-slate-900" />}>
    {byGrade.length === 0 ? (
      <Empty testId={`sales-${kind}-empty`} text={`No ${label} dispatched this month.`} />
    ) : (
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold mb-2">Grade wise</p>
          <BarList rows={byGrade} labelKey="grade" color={color} testId={`sales-${kind}-grades`} />
        </div>
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold mb-2">Top 5 customers (qty)</p>
          <TopCustomers rows={top} testId={`sales-${kind}-top-customers`} />
        </div>
      </div>
    )}
  </Section>
);

export default function ExecutiveDashboard() {
  const { data, isLoading, error } = useQuery({ queryKey: ["executive-dashboard"], queryFn: fetchExecutiveDashboard, refetchInterval: 60_000 });

  if (isLoading) {
    return <div className="flex items-center justify-center h-64" data-testid="exec-loading"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }
  if (error || !data) {
    return <div data-testid="exec-error" className="bg-red-50 border border-red-200 rounded-sm p-6 text-sm text-red-800">Couldn't load the director dashboard.</div>;
  }
  const s = data.sales_month;

  return (
    <div data-testid="exec-dashboard" className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-slate-200 pb-4">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold mb-1">Director's desk</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-slate-900">Business at a glance</h2>
        </div>
        <div className="flex gap-2 text-xs">
          <span data-testid="exec-month-label" className="px-3 h-8 inline-flex items-center rounded-sm bg-slate-900 text-white font-semibold">{data.month_label}</span>
          <span data-testid="exec-fy-label" className="px-3 h-8 inline-flex items-center rounded-sm border border-slate-300 text-slate-700 font-semibold">{data.fy_label}</span>
        </div>
      </div>

      <Section testId="purchase-section" icon={PackagePlus} eyebrow={`Purchase · ${data.month_label}`} title="RM intake by supplier & grade"
        right={<div className="text-right"><T kg={data.purchase.total_kg} testId="purchase-total" className="text-2xl font-black text-slate-900" /><p className="text-[11px] text-slate-500">{data.purchase.total_coils} coils</p></div>}>
        <IntakePivot rows={data.purchase.rm_intake} />
      </Section>

      <div className="grid xl:grid-cols-2 gap-6">
        <SalesCard kind="sfg" label="SFG" color={SFG_COLOR} byGrade={s.sfg_by_grade} top={s.sfg_top_customers} total={s.sfg_total_kg} />
        <SalesCard kind="fg" label="FG" color={FG_COLOR} byGrade={s.fg_by_grade} top={s.fg_top_customers} total={s.fg_total_kg} />
      </div>

      <Section testId="monthly-section" icon={TrendingUp} eyebrow={`Sales · ${data.fy_label}`} title="Monthly sales (SFG + FG)">
        <MonthlyChart data={data.monthly} />
      </Section>

      <div className="grid lg:grid-cols-3 gap-6">
        <Section testId="quarterly-section" icon={CalendarRange} eyebrow={data.fy_label} title="Quarterly sales" className="lg:col-span-2">
          <QuarterTiles quarters={data.quarterly} />
        </Section>
        <Section testId="yearly-section" icon={History} eyebrow="Financial years" title="Yearly sales">
          <YearlyTable years={data.yearly} />
        </Section>
      </div>

      <Section testId="rm-stock-section" icon={Warehouse} eyebrow="Not yet given to slitting" title="RM in stock by grade"
        right={<div className="text-right"><T kg={data.rm_stock.total_kg} testId="rm-stock-total" className="text-2xl font-black text-slate-900" /><p className="text-[11px] text-slate-500">{data.rm_stock.total_coils} coils</p></div>}>
        <RmStockTable rows={data.rm_stock.by_grade} />
      </Section>
    </div>
  );
}
